import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import { Submission } from '../../submission.js';
import { countCorrectionModeUncertainties } from '../grading-uncertainties.js';
import { createGradingFinishedPendingTask, type GradingFinishedCause } from './grading-finished.task.js';
import { pushWorkflowTask } from './workflow-agent.js';

/**
 * Announce to the agent that a background (re)grade has finished. The reupload paths dispatch
 * silent per-submission re-grades; call this once they've all completed so the agent can inform the
 * user — with wording driven by `causes` (new submissions vs. a master-solution/grading-scheme
 * change). Mirrors the grading tool's own grading-finished announcement, including the open-
 * uncertainty count that decides whether to offer the correction canvas.
 *
 * @param causes Everything this one re-grade was based on; all of them are named in the announcement.
 * @param submissionIds Which submissions were (re)graded. Omit to count all submissions of the exam.
 * @param taskNumbers Which (main) tasks were regraded — names them in the announcement and scopes
 *   the uncertainty count to them. Omit for a full regrade to default to every task of the exam.
 */
export async function announceRegradeFinished(opts: {
  examId: string;
  causes: GradingFinishedCause[];
  submissionIds?: string[];
  taskNumbers?: string[];
  /** See GradingFinishedTaskOptions.previouslyApprovedCount — only meaningful for `manual_grading_insight`. */
  previouslyApprovedCount?: number;
}): Promise<void> {
  const { examId, causes } = opts;
  if (!examId) return;

  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any);
  if (!exam) return;

  const conversationId = String((exam as any).conversationId ?? '').trim();
  if (!conversationId) {
    logger.warn(`[Regrade] ${examId}: no conversationId on exam — cannot announce regrade`);
    return;
  }

  // A partial (refinement-triggered) regrade only touches specific tasks, so name exactly those.
  // A full regrade (e.g. a reuploaded master solution) omits them and defaults to every task.
  const taskNumbers = opts.taskNumbers?.length
    ? opts.taskNumbers
    : Array.isArray((exam as any).tasks)
      ? (exam as any).tasks.map((task: any) => String(task?.taskNumber ?? '')).filter(Boolean)
      : [];

  const submissionCollection = await getCollection(Submission);
  const filter = opts.submissionIds?.length
    ? { _id: { $in: opts.submissionIds }, examId }
    : { examId };
  const submissions = await submissionCollection
    .find(filter as any, { projection: { taskSubmissions: 1 } } as any)
    .toArray();

  const gradedCount = opts.submissionIds?.length ?? submissions.length;
  const uncertaintyCounts = countCorrectionModeUncertainties(submissions, taskNumbers);

  await pushWorkflowTask(
    examId,
    conversationId,
    createGradingFinishedPendingTask({
      taskNumbers,
      gradedCount,
      uncertaintiesTotal: uncertaintyCounts.total,
      shouldSuggestCanvas: uncertaintyCounts.total > 0,
      causes,
      previouslyApprovedCount: opts.previouslyApprovedCount,
    }),
  );
}
